import { LOCAL_ARIZONA_COUNTIES_URL, LOCAL_ARIZONA_NON_ZIP_URL, LOCAL_ARIZONA_ZCTAS_URL, LOCAL_NEIGHBOR_ZCTAS_URL, MAP_LAB_NON_ZIP_HATCH_IMAGE, MAP_LAB_OUTSIDE_MASK_OUTER_RING } from "./constants.js";
import { reportCountsByZip } from "./data.js";

export let zipGeometryLoadPromise = null;

export let countyGeometryLoadPromise = null;

export let nonZipGeometryLoadPromise = null;

export let neighborZipGeometryLoadPromise = null;

export let countyBoundsCache = null;

export function diagonalHatchImageData(size, spacing, lineColor, lineWidth) {
  const canvas = document.createElement("canvas");
  canvas.width = size;
  canvas.height = size;
  const context = canvas.getContext("2d");
  if (!context) return null;
  context.clearRect(0, 0, size, size);
  context.strokeStyle = lineColor;
  context.lineWidth = lineWidth;
  for (let offset = -size; offset < size * 2; offset += spacing) {
    context.beginPath();
    context.moveTo(offset, 0);
    context.lineTo(offset + size, size);
    context.stroke();
  }
  return context.getImageData(0, 0, size, size);
}


export function ensureNonZipHatchImage(map) {
  if (map.hasImage(MAP_LAB_NON_ZIP_HATCH_IMAGE)) return;
  // 28px tile at pixelRatio 2 -> a 14px on-screen tile with one bold slate line each,
  // ~10px of clear space between lines. Reads as distinct diagonals even far zoomed out.
  const image = diagonalHatchImageData(28, 28, "rgba(100, 116, 139, 0.85)", 2.6);
  if (image) map.addImage(MAP_LAB_NON_ZIP_HATCH_IMAGE, image, { pixelRatio: 2 });
}

// PREVIEW: add a clustered source + cluster / count / point layers for a category.
// Clusters are a colored circle with the count centered; individual points are a
// smaller colored circle in the same category color.

export function emptyFeatureCollection() {
  return {
    type: "FeatureCollection",
    features: [],
  };
}


export async function loadArizonaZipGeometry() {
  if (zipGeometryLoadPromise) return zipGeometryLoadPromise;
  zipGeometryLoadPromise = fetchGeoJson(LOCAL_ARIZONA_ZCTAS_URL);
  return zipGeometryLoadPromise;
}


export async function loadArizonaNonZipGeometry() {
  if (nonZipGeometryLoadPromise) return nonZipGeometryLoadPromise;
  nonZipGeometryLoadPromise = fetchGeoJson(LOCAL_ARIZONA_NON_ZIP_URL);
  return nonZipGeometryLoadPromise;
}


// Out-of-state ZIP outlines for the dimmed surroundings. Purely decorative, so a failed
// fetch resolves to an empty collection rather than rejecting: losing the backdrop should
// never take the map's data layers down with it.
export async function loadNeighborZipGeometry() {
  if (neighborZipGeometryLoadPromise) return neighborZipGeometryLoadPromise;
  neighborZipGeometryLoadPromise = fetchGeoJson(LOCAL_NEIGHBOR_ZCTAS_URL).catch(() => emptyFeatureCollection());
  return neighborZipGeometryLoadPromise;
}


// The gray "everything is dimmed" backdrop of the spotlight mask (see MAP_LAB_OUTSIDE_MASK_*
// in constants): a single plain rectangle covering the whole pannable area. Arizona is
// restored on top by repainting the county polygons in the light base tone — see the
// two-layer note in constants.js — so this rectangle needs no holes.
export function outsideArizonaMaskRectFeatureCollection() {
  return {
    type: "FeatureCollection",
    features: [
      {
        type: "Feature",
        properties: {},
        geometry: {
          type: "Polygon",
          coordinates: [MAP_LAB_OUTSIDE_MASK_OUTER_RING],
        },
      },
    ],
  };
}


export async function loadArizonaCountyGeometry() {
  if (countyGeometryLoadPromise) return countyGeometryLoadPromise;
  countyGeometryLoadPromise = fetchGeoJson(LOCAL_ARIZONA_COUNTIES_URL).then((geojson) => {
    countyBoundsCache = countyBoundsByName(geojson);
    return geojson;
  });
  return countyGeometryLoadPromise;
}

// PREVIEW (Map Lab county shading): per-county [[w,s],[e,n]] bbox for drill-down
// fitBounds, computed once from the county geometry.

export function countyBoundsByName(geojson) {
  const bounds = {};
  (geojson.features || []).forEach((feature) => {
    const name = feature.properties?.NAME;
    if (!name) return;
    bounds[name] = geometryBounds(feature.geometry);
  });
  return bounds;
}


export function geometryBounds(geometry) {
  let west = Infinity;
  let south = Infinity;
  let east = -Infinity;
  let north = -Infinity;
  const polygons = geometry?.type === "MultiPolygon" ? geometry.coordinates : [geometry?.coordinates || []];
  polygons.forEach((polygon) => {
    (polygon[0] || []).forEach(([lng, lat]) => {
      if (lng < west) west = lng;
      if (lng > east) east = lng;
      if (lat < south) south = lat;
      if (lat > north) north = lat;
    });
  });
  return [
    [west, south],
    [east, north],
  ];
}


export async function fetchGeoJson(url) {
  const response = await fetch(url);
  if (!response.ok) throw new Error(`${url} returned HTTP ${response.status}`);
  const geojson = await response.json();
  if (geojson.type !== "FeatureCollection" || !Array.isArray(geojson.features)) {
    throw new Error(`${url} did not return a GeoJSON FeatureCollection`);
  }
  return geojson;
}

// PREVIEW (Map Lab shading): the robust "max" anchoring the relative opacity scale — the
// value at MAP_LAB_ROBUST_MAX_PERCENTILE of the positive metric values, so one runaway
// area doesn't define the top of the scale and flatten everyone else. Returns 0 when
// there are no positive values (opacity then falls back to the suppression floor).

export function zipCentroidGeoJson(zipGeometry, zipCounts = reportCountsByZip()) {
  const geometryByZip = new Map(
    (zipGeometry.features || []).map((feature) => [feature.properties?.GEOID || feature.properties?.ZCTA5, feature])
  );
  const features = [];
  zipCounts.forEach((zipCount) => {
    if ((zipCount.total_count || 0) <= 0) return;
    const zipFeature = geometryByZip.get(zipCount.zip_code);
    if (!zipFeature) return;
    features.push({
      type: "Feature",
      geometry: { type: "Point", coordinates: featureInteriorPoint(zipFeature) },
      properties: {
        zip_code: zipCount.zip_code,
        total_count: zipCount.total_count,
        sick_count: zipCount.sick_count,
        healthy_count: zipCount.healthy_count,
        people_sick_count: zipCount.people_sick_count,
        sick_rate: (zipCount.sick_count || 0) / zipCount.total_count,
      },
    });
  });
  return { type: "FeatureCollection", features };
}


export function polygonRings(geometry) {
  if (!geometry) return [];
  if (geometry.type === "Polygon") return geometry.coordinates || [];
  if (geometry.type === "MultiPolygon") return (geometry.coordinates || []).flat();
  return [];
}


export function largestRing(rings) {
  return rings
    .filter((ring) => Array.isArray(ring) && ring.length > 3)
    .sort((a, b) => Math.abs(ringArea(b)) - Math.abs(ringArea(a)))[0];
}


export function ringArea(ring) {
  let area = 0;
  for (let index = 0; index < ring.length - 1; index += 1) {
    area += ring[index][0] * ring[index + 1][1] - ring[index + 1][0] * ring[index][1];
  }
  return area / 2;
}


export function ringBounds(ring) {
  return ring.reduce(
    (bounds, point) => ({
      minX: Math.min(bounds.minX, point[0]),
      minY: Math.min(bounds.minY, point[1]),
      maxX: Math.max(bounds.maxX, point[0]),
      maxY: Math.max(bounds.maxY, point[1]),
    }),
    { minX: Infinity, minY: Infinity, maxX: -Infinity, maxY: -Infinity }
  );
}


export function featureInteriorPoint(feature) {
  const lon = Number(feature.properties?.INTPTLON || feature.properties?.CENTLON);
  const lat = Number(feature.properties?.INTPTLAT || feature.properties?.CENTLAT);
  if (Number.isFinite(lon) && Number.isFinite(lat)) return [lon, lat];
  const ring = largestRing(polygonRings(feature.geometry));
  if (!ring) return [-111.9, 34.25];
  const bounds = ringBounds(ring);
  return [(bounds.minX + bounds.maxX) / 2, (bounds.minY + bounds.maxY) / 2];
}
